<template>
  <div>
    <confirm-modal
      v-if="showExcelDownloadModal"
      id="excel-download-modal"
      title="Information"
      :confirmation="confirmModalMessage"
      ok-button="Close"
      close-button=""
      @submit="showExcelDownloadModal = false"
    />
    <edit-parcel-items
      v-if="selectedEditParcel"
      id="edit-parcel-items"
      :parcel="selectedEditParcel"
      :declaration="computedDeclarationData"
      :shipment="shipment"
      :readonly="!canEdit"
    />
    <status-information
      v-if="informationItem"
      :item="informationItem"
    />
    <resend-declarations
      v-if="shipment.id"
      id="resend-declarations"
      :customs="shipment.entry_country"
      :shipment="shipment"
      :declaration-type="declarationType"
      status="draft_created"
    />
    <b-row>
      <b-col class="mt-2">
        <b-card-text>
          Declarations submitted to customs in draft mode. Add missing details and changes using customs portal.
        </b-card-text>
      </b-col>
      <b-col
        class="text-right"
      >
        <b-button
          variant="outline-success"
          class="m-1"
          @click="handleExcelDownload"
        >
          Download as Excel
        </b-button>
        <b-button
          v-if="canEdit"
          v-b-modal.resend-declarations
          variant="outline-success"
          class="m-1"
        >
          Resend all to customs
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <div class="overflow-x-scroll">
          <b-table
            :items="parcels"
            responsive
            :fields="fields"
            :busy="loading"
            :sort-by.sync="sortBy"
            :sort-desc.sync="isSortDirDesc"
            no-local-sorting
            show-empty
            empty-text="No matching records found"
            class="mb-0"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle mr-1" />
                <strong>Loading...</strong>
              </div>
            </template>

            <template #head()="data">
              <span class="text-nowrap">
                {{ data.label }}
              </span>
            </template>

            <template #cell(actions)="data">
              <b-button
                v-if="canEdit"
                v-b-modal.edit-parcel-items
                type="button"
                variant="warning"
                @click="editDeclaration(data.item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
            </template>

            <template #cell(tracking_number)="data">
              {{ data.item.parcel.tracking_number }}
            </template>

            <template #cell(container_code)="data">
              {{ data.item.parcel.container_code }}
            </template>

            <template #cell(descriptions)="data">
              <div>
                {{ data.item.parcel.items.map(function (parcelItem) {return parcelItem.description}).join(', ') }}
              </div>
            </template>

            <template #cell(hs_codes)="data">
              <div>
                <b-row
                  v-for="(parcelItem, index) in data.item.parcel.items"
                  :key="index"
                >
                  <span
                    v-if="getItemError(index, data.item.status_info, parcelItem.hs_code)"
                    style="color: #780000; font-weight: bold;"
                  >
                    {{ parcelItem.hs_code }}
                  </span>
                  <span v-if="!getItemError(index, data.item.status_info, parcelItem.hs_code)">{{ parcelItem.hs_code }}</span>
                </b-row>
              </div>
            </template>

            <template #cell(value)="data">
              <div
                v-if="data.item.parcel.items.length > 0"
                class="text-right"
              >
                {{ data.item.parcel.items.reduce(function (sum, parcelItem) {return sum + parseFloat(parcelItem.value)}, 0) | formatNumber(2) }}
              </div>
            </template>

            <template #cell(status_info)="data">
              <div v-if="data.item.status_info !== null && data.item.status_info['errors']">
                <span
                  v-b-modal.modal-status-information
                  class="text-primary text-nowrap"
                  @click="informationItem = data.item"
                >
                  Show errors
                  <b-badge
                    variant="light"
                    class="text-primary"
                  >
                    {{ data.item.status_info['errors'].length }}
                  </b-badge>
                </span>
              </div>
            </template>

          </b-table>
        </div>
        <pagination
          v-show="!loading"
          :per-page="perPage"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCardText,
  BCol,
  BRow,
  BTable,
  BButton,
  BSpinner,
  BBadge,
} from 'bootstrap-vue'
import EditParcelItems from '@/views/shipments/declarations/modals/EditParcelItems.vue'
import Pagination from '@/layouts/components/Pagination/Pagination.vue'
import StatusInformation from '@/views/shipments/declarations/modals/StatusInformation.vue'
import ResendDeclarations from '@/views/shipments/declarations/modals/ResendDeclarations.vue'
import { EventBus } from '@core/utils/utils'
import { searchQuery } from '@core/utils/filter'
import ConfirmModal from '@core/components/confirmation/confirmModal.vue'
import excelDownloadMixin from '@/mixins/excelDownloadMixin'
import moment from 'moment'

export default {
  components: {
    ConfirmModal,
    ResendDeclarations,
    BBadge,
    StatusInformation,
    Pagination,
    BCardText,
    BCol,
    BRow,
    BTable,
    BButton,
    BSpinner,
    EditParcelItems,
  },
  mixins: [excelDownloadMixin],
  props: ['shipmentId', 'shipment', 'filters', 'declarationType'],
  data() {
    const fields = [
      { key: 'actions', label: 'Actions' },
      { key: 'tracking_number', label: 'Tracking code', sortable: true },
      { key: 'container_code', label: 'Container', sortable: true },
      { key: 'descriptions', label: 'Description' },
      { key: 'hs_codes', label: 'HS code' },
      { key: 'value', label: 'Value' },
      { key: 'lrn', label: 'LRN' },
      { key: 'mrn', label: 'MRN' },
      { key: 'status_info', label: 'Errors' },
    ]
    return {
      loading: false,
      informationItem: null,
      page: 1,
      perPage: 100,
      selectedEditParcel: null,
      selectedEditDeclaration: null,
      sortBy: null,
      isSortDirDesc: null,
      fields: fields,
      parcels: [],
      excelDownloadUrl: `${process.env.VUE_APP_API_URL}/v1/companies/${this.shipment.company.id}/shipments/${this.shipmentId}/declarations/${this.declarationType}/draftCreated/excel`,
    }
  },
  computed: {
    excelFileName() {
      const timestamp = moment().format('YYYY-MM-DD-HH-mm-ss')
      return `draft-created-declarations-list-${timestamp}.xlsx`
    },
    canEdit() {
      return this.$permissions().hasPermission('edit shipment')
    },
    computedDeclarationData() {
      if (this.selectedEditDeclaration !== null) {
        return this.selectedEditDeclaration
      }
      return null
    },
    url() {
      let url = `/v1/companies/${this.$activeCompany().data.company.id}/shipments/${this.shipmentId}/declarations/${this.declarationType}/draft-created`
      url += `?page=${this.page}`
      url += `&per_page=${this.perPage}`
      return url
    },
  },
  watch: {
    filters() {
      this.fetchData()
    },
    sortBy() {
      this.fetchData()
    },
    isSortDirDesc() {
      this.fetchData()
    },
  },
  created() {
    EventBus.$on('update-parcel-items', this.updateParcelItems)
    EventBus.$on('general-declaration-update', this.fetchData)
    this.fetchData()
  },
  mounted() {
    EventBus.$on('pageChange', this.handlePageChange)
    EventBus.$on('renewDeclarationList', this.handleRenewDeclarationList)
  },
  beforeDestroy() {
    EventBus.$off('update-parcel-items', this.updateParcelItems)
    EventBus.$off('general-declaration-update', this.fetchData)
    EventBus.$off('pageChange', this.handlePageChange)
    EventBus.$off('renewDeclarationList', this.handleRenewDeclarationList)
  },
  methods: {
    handleRenewDeclarationList() {
      this.fetchData()
      EventBus.$emit('refresh-statistics')
    },
    handlePageChange(newPage) {
      this.page = newPage
      this.fetchData()
    },
    canSeeErrors() {
      return this.$permissions().hasRole('Feeport Admin')
    },
    getItemError(index, errorList, hsCode) {
      let result = false
      if (errorList !== null) {
        errorList.errors.forEach(error => {
          if (error.item_reference && parseInt(error.item_reference, 10) === index + 1) {
            result = error
          } else if (hsCode && error.declaration_value === hsCode && error.field === 'parcel_item.hs_code') {
            result = error
          }
        })
      }

      return result
    },
    updateParcelItems(parcelId) {
      this.$http.put(`/v1/companies/${this.$activeCompany().data.company.id}/shipments/${this.shipmentId}/declarations`, { parcels: [parcelId] })
      this.selectedEditParcel = null
      this.selectedEditDeclaration = null
      this.fetchData()
      EventBus.$emit('refresh-statistics')
    },
    editDeclaration(data) {
      this.selectedEditParcel = data.parcel
      this.selectedEditDeclaration = data
    },
    fetchData(activeTab, showLoader = true) {
      if ((!activeTab || activeTab === 'draft-created-h1') && !this.loading) {
        if (showLoader) {
          this.loading = true
        }
        this.$http.get(searchQuery(this.url, this.filters), {
          params: {
            sortBy: this.sortBy,
            sortDesc: this.isSortDirDesc,
          },
        })
          .then(response => {
            this.loading = false
            this.parcels = response.data.data
            EventBus.$emit('updateMeta', response.data.meta)
          })
      }
    },
  },
}
</script>
